// 🩺 B375 (AI_156) — MELD/CHILD NO PÓS-TRANSPLANTE: APAGAR SEM DESTRUIR
// ─────────────────────────────────────────────────────────────
// Regra clínica de Leonardo (CRM-RO 3793, AI_89): MELD e Child-Pugh medem a
// gravidade da CIRROSE. Depois do transplante a cirrose nativa não existe mais,
// então esses escores não descrevem o paciente ATUAL. O sistema tem uma rede
// determinística que remove menções residuais escapadas do prompt.
//
// O que estava errado (achado por Leonardo na leitura cega do B363, 30/10/2026):
// a rede era CEGA e mordia o texto. Casos reais, nas análises geradas:
//   "…trombose de porta, com 0 de 26 e Child-Pugh C12 em 20/12/2019"
//   "Antes do transplante, em 20/12/2019, era e 0 26."
// Causas somadas:
//  1. a regex casava "MELD 3" e engolia o "." de "MELD 3.0" — rótulo que o
//     B358-FU2 passou a usar — deixando um "0" órfão colado no valor real;
//  2. apagava a história PRÉ-transplante, que o nosso próprio desenho pede que
//     seja citada (B239/AI_107: "cite brevemente a história pré-Tx");
//  3. era assimétrica: "Child-Pugh C" saía, "Child-Pugh C12" ficava.
// Um "0" solto perto de um número é PIOR que a menção original: pode ser lido
// como MELD zero num documento clínico.
//
// Desenho novo, em três compromissos:
//  • FRASE A FRASE. Frase marcada como histórica (pré-Tx, antes do transplante,
//    ou com data anterior ao transplante) fica INTACTA.
//  • Fora dela, o escore sai INTEIRO — rótulo, versão ("3.0"), conectivo ("de")
//    e valor — nunca pela metade.
//  • SEM RESTO: conectivos órfãos e pontuação dupla são limpos, e nenhum dígito
//    solto pode sobrar (há teste para isso).
// ─────────────────────────────────────────────────────────────

/** Marcadores de que a frase fala do PASSADO (pré-transplante). */
const MARCA_HISTORICA =
  /pr[ée][-\s]?(?:tx|transplante|operat[óo]ri[oa])|antes\s+do\s+transplante|hist[óo]ric|na\s+[ée]poca|a[oà]\s+(?:tempo\s+da\s+)?inscri[çc][ãa]o|em\s+lista\s+de\s+espera|previamente\s+ao\s+transplante/i;

// MELD, com ou sem "-Na", com ou sem a versão "3.0", com ou sem "de": o valor
// vai junto. `\d{1,2}` porque MELD vai de 6 a 40 — não casa ano nem data.
const MELD =
  /\bMELD(?:[-\s]?(?:Na|S[óo]dio))?(?:\s*3[.,]0)?(?:\s*(?:calculado|estimado))?\s*(?::|=|de|era|foi)?\s*(\d{1,2})\b(?!\s*\/)(?:\s*\([^)]{0,40}\))?/gi;

// Child-Pugh: "classe C", "C", "C12", "C (12 pontos)", "12 pontos".
const CHILD =
  /\bChild[-\s]*(?:Pugh)?\s*(?::|=)?\s*(?:classe\s*)?(?:[ABC]\s*\d{0,2}|\d{1,2}\s*(?:pontos|pts))\b(?:\s*\(?\s*\d{1,2}\s*(?:pontos|pts)\s*\)?)?/gi;

/** Sobrou conectivo/pontuação pendurada onde o escore saiu? Limpa. */
function limparResto(frase: string): string {
  return (
    frase
      // "era e 0 26" / ", com  e " → conectivos duplicados ou órfãos
      .replace(/\b(com|e|de|em)\s+(?=[,;.]|$)/gi, "")
      .replace(/\b(era|foi|tinha)\s+(?=[,;.]|$)/gi, "")
      .replace(/\b(e|com)\s+(?=\b(?:e|com)\b)/gi, "")
      // pontuação órfã
      .replace(/\s*,\s*(?=[,;.])/g, "")
      .replace(/\(\s*\)/g, "")
      .replace(/\s*,\s*\./g, ".")
      .replace(/\s+([,;.])/g, "$1")
      .replace(/ {2,}/g, " ")
      .trim()
  );
}

/** Datas dd/mm/aaaa da frase, como ISO, para comparar com o transplante. */
function datasDaFrase(frase: string): string[] {
  const out: string[] = [];
  const re = /\b(\d{2})\/(\d{2})\/(\d{4})\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(frase)) !== null) out.push(`${m[3]}-${m[2]}-${m[1]}`);
  return out;
}

/** A frase se refere ao passado pré-transplante? */
export function fraseEhHistorica(frase: string, dataTransplanteISO?: string): boolean {
  if (MARCA_HISTORICA.test(frase)) return true;
  if (dataTransplanteISO) {
    // Data anterior ao transplante na mesma frase = está falando do pré-Tx.
    return datasDaFrase(frase).some((d) => d < dataTransplanteISO);
  }
  return false;
}

/**
 * Remove MELD/Child apresentados como condição ATUAL de um paciente
 * transplantado. Preserva frases históricas. Nunca deixa número órfão.
 */
export function removerEscoresAtuaisPosTx(
  texto: string,
  dataTransplanteISO?: string
): string {
  if (!texto) return texto;
  // Quebra em frases preservando o delimitador e as quebras de linha.
  const pedacos = texto.split(/(?<=[.;!?])(\s+)|(\n+)/g).filter((p) => p !== undefined);
  return pedacos
    .map((pedaco) => {
      if (!/\bMELD\b|\bChild\b/i.test(pedaco)) return pedaco;
      if (fraseEhHistorica(pedaco, dataTransplanteISO)) return pedaco;
      const limpo = pedaco.replace(MELD, "").replace(CHILD, "");
      return limparResto(limpo);
    })
    .join("");
}
